/// <amd-module name="Search/Code/Components/ItemCommands" />

import * as React from "react";
import * as Resources from "Search/Code/Resources";
import { IIconProps } from "VSSUI/Icon";
import { IMenuItem } from "VSSUI/Menu";
import { CodeResult } from "Search/LegacyWebApi/Contracts/Code";
import { isVCType } from "Search/Code/Utils";
import { ItemCommandKeys } from "Search/Code/Constants";
import { copyToClipboard } from "VSSUI/Utils/ClipboardUtils";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

export interface ICommandsOptions {
    item: CodeResult;

    itemUrl: string | undefined;

    onMenuItemInvoked?: (menuItem: IMenuItem) => void;
}

interface ICommandCreator {
    (options: ICommandsOptions, index: number): IMenuItem;
}

export function getCommandsInContextMenu(options: ICommandsOptions): IMenuItem[] {
    const { item } = options;
    if (!item) {
        return [];
    }

    const commands = isVCType(item.vcType)
        ? create(nonSourceDepotResultCommandCreators, options)
        : create(sourceDepotResultCommandCreators, options);

    return commands;
}

export const creators = {
    browseFile: (options: ICommandsOptions, index: number) => ({
        id: ItemCommandKeys.browseFile,
        text: Resources.OpenFileInNewTabMenuItem,
        iconProps: getMenuIcon("ReplyMirrored"),
        onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            const { onMenuItemInvoked } = options;
            window.open(options.itemUrl, "_blank");
            if (onMenuItemInvoked && menuItem) {
                onMenuItemInvoked(menuItem);
            }
        }
    }),
    download: (options: ICommandsOptions, index: number) => ({
        id: ItemCommandKeys.download,
        text: Resources.DownloadText,
        iconProps: getMenuIcon("Download"),
        data: {
            item: options.item
        },
        onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            const { onMenuItemInvoked } = options;
            if (onMenuItemInvoked && menuItem) {
                onMenuItemInvoked(menuItem);
            };
        }
    }),
    copyPath: (options: ICommandsOptions, index: number) => ({
        id: ItemCommandKeys.copyPath,
        text: Resources.CopyFilePathMenuItem,
        iconProps: getMenuIcon("Copy"),
        onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            const { onMenuItemInvoked, item } = options;
            
            copyToClipboard(item.path);
            if (onMenuItemInvoked && menuItem) {
                onMenuItemInvoked(menuItem);
            }
        }
    }),
    linkToFile: (options: ICommandsOptions, index: number) => ({
        id: ItemCommandKeys.linkToFile,
        text: Resources.CopyFileURLMenuItem,
        iconProps: getMenuIcon("Link"),
        onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            const { onMenuItemInvoked, itemUrl } = options;
            // itemUrl only contains the path of the file relative to the host.
            // Get the protocol and hostname using "location.origin" property.
            // "/user/file.txt" --> "https://www.abc.com/user/file.txt"
            const linkToFileUrl = itemUrl ? combineUrlPaths(location.origin, itemUrl) : "";
            copyToClipboard(linkToFileUrl);
            if (onMenuItemInvoked && menuItem) {
                onMenuItemInvoked(menuItem);
            }
        }
    })
};

const nonSourceDepotResultCommandCreators: ICommandCreator[] = [
    creators.browseFile,
    creators.download,
    creators.copyPath,
    creators.linkToFile
];

const sourceDepotResultCommandCreators: ICommandCreator[] = [
    creators.download,
    creators.copyPath
];

function getMenuIcon(name: string): IIconProps {
    return { iconName: name };
}

function create(creators: ICommandCreator[], options: ICommandsOptions): IMenuItem[] {
    return creators
        .map((creator, index) => creator(options, index))
        .filter(command => command);
}
